import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api, { JudgmentDetail } from '../services/api';
import InlineNoteEditor from './InlineNoteEditor';
import { useBookmarks } from '../hooks/useBookmarks';
import { useAuth } from '../context/AuthContext';

interface JudgmentModalProps {
  judgmentId: number;
  onClose: () => void;
  bookmarkNotes?: string | null;
  onNotesUpdate?: (notes: string) => Promise<void>;
}

export default function JudgmentModal({ judgmentId, onClose, bookmarkNotes, onNotesUpdate }: JudgmentModalProps) {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const { isBookmarked, toggleBookmark, checkBookmarks } = useBookmarks();
  const [judgment, setJudgment] = useState<JudgmentDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check bookmark status for this judgment
  useEffect(() => {
    if (isAuthenticated) {
      checkBookmarks([judgmentId]);
    }
  }, [isAuthenticated, judgmentId, checkBookmarks]);

  useEffect(() => {
    const fetchJudgment = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await api.getJudgment(judgmentId);
        setJudgment(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : t('errors.loadJudgment'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchJudgment();
  }, [judgmentId]);

  useEffect(() => {
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    // Close on Escape key
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const { i18n } = useTranslation();
  const courtTypeKey = (code: string) => `courtType.${code}`;

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(i18n.language === 'pl' ? 'pl-PL' : 'en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal container */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="sticky top-0 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4 flex items-center justify-between">
            <div>
              {judgment && (
                <>
                  <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                    {judgment.caseNumber || t('judgmentModal.ruling')}
                  </h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {t(courtTypeKey(judgment.courtType), judgment.courtType)}
                    {judgment.judgmentDate && ` • ${formatDate(judgment.judgmentDate)}`}
                  </p>
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              {isAuthenticated && (
                <button
                  onClick={() => toggleBookmark(judgmentId)}
                  className={`p-2 rounded-lg transition-colors ${
                    isBookmarked(judgmentId)
                      ? 'text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30'
                      : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                  }`}
                  title={isBookmarked(judgmentId) ? t('judgmentModal.removeBookmark') : t('judgmentModal.addBookmark')}
                >
                  <svg
                    className="w-5 h-5"
                    fill={isBookmarked(judgmentId) ? 'currentColor' : 'none'}
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
                    />
                  </svg>
                </button>
              )}
              <button
                onClick={onClose}
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                aria-label={t('judgmentModal.closeModal')}
              >
                <svg
                  className="w-5 h-5 text-slate-500 dark:text-slate-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-4 overflow-y-auto max-h-[calc(90vh-120px)]">
            {isLoading ? (
              <div className="space-y-4 animate-pulse">
                <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-3/4" />
                <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-full" />
                <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-5/6" />
                <div className="h-32 bg-slate-200 dark:bg-slate-700 rounded" />
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 mx-auto mb-4 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-red-500 dark:text-red-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <p className="text-slate-600 dark:text-slate-400">{error}</p>
              </div>
            ) : judgment ? (
              <div className="space-y-6">
                {/* User Notes */}
                {onNotesUpdate && (
                  <section className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-blue-700 dark:text-blue-300 uppercase tracking-wider mb-2">
                      {t('judgmentModal.yourNote')}
                    </h3>
                    <InlineNoteEditor
                      value={bookmarkNotes ?? null}
                      onSave={onNotesUpdate}
                      placeholder={t('judgmentModal.notePlaceholder')}
                    />
                  </section>
                )}

                {/* Summary */}
                {judgment.summary && (
                  <section>
                    <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                      {t('judgmentModal.summary')}
                    </h3>
                    <p className="text-slate-700 dark:text-slate-300">{judgment.summary}</p>
                  </section>
                )}

                {/* Legal Bases */}
                {judgment.legalBases && judgment.legalBases.length > 0 && (
                  <section>
                    <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                      {t('judgmentModal.legalBases')}
                    </h3>
                    <ul className="list-disc list-inside space-y-1">
                      {judgment.legalBases.map((base, index) => (
                        <li key={index} className="text-slate-700 dark:text-slate-300 text-sm">
                          {typeof base === 'string' ? base : JSON.stringify(base)}
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                {/* Judges */}
                {judgment.judges && judgment.judges.length > 0 && (
                  <section>
                    <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                      {t('judgmentModal.judges')}
                    </h3>
                    <ul className="space-y-1">
                      {judgment.judges.map((judge, index) => (
                        <li key={index} className="text-slate-700 dark:text-slate-300">
                          {judge}
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                {/* Keywords */}
                {judgment.keywords && judgment.keywords.length > 0 && (
                  <section>
                    <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                      {t('judgmentModal.keywords')}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {judgment.keywords.map((keyword, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-sm rounded"
                        >
                          {typeof keyword === 'string' ? keyword : JSON.stringify(keyword)}
                        </span>
                      ))}
                    </div>
                  </section>
                )}

                {/* Source URL */}
                {judgment.source && (
                  <section>
                    <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                      {t('judgmentModal.source')}
                    </h3>
                    <div className="flex items-center gap-3 flex-wrap">
                      {/* For common courts, construct orzeczenia.ms.gov.pl URL from judgmentId */}
                      {judgment.source.code === 'COMMON_COURT' && judgment.source.judgmentId ? (
                        <a
                          href={`https://orzeczenia.ms.gov.pl/details/$N/${judgment.source.judgmentId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                            />
                          </svg>
                          <span>{t('judgmentModal.openInPortal')}</span>
                        </a>
                      ) : judgment.source.judgmentUrl ? (
                        <a
                          href={judgment.source.judgmentUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                            />
                          </svg>
                          <span>
                            {judgment.source.code === 'SUPREME_COURT'
                              ? t('judgmentModal.searchInSN')
                              : judgment.source.code === 'CONSTITUTIONAL_TRIBUNAL'
                              ? t('judgmentModal.searchInTK')
                              : judgment.source.code === 'NATIONAL_APPEAL_CHAMBER'
                              ? t('judgmentModal.searchInKIO')
                              : judgment.source.code === 'CBOSA'
                              ? t('judgmentModal.openInCBOSA')
                              : t('judgmentModal.openSource')}
                          </span>
                        </a>
                      ) : (
                        <span className="text-slate-600 dark:text-slate-400 text-sm">
                          {judgment.source.code === 'SUPREME_COURT' ? t('courtType.SUPREME') :
                           judgment.source.code === 'CONSTITUTIONAL_TRIBUNAL' ? t('courtType.CONSTITUTIONAL_TRIBUNAL') :
                           judgment.source.code === 'NATIONAL_APPEAL_CHAMBER' ? t('courtType.NATIONAL_APPEAL_CHAMBER') :
                           judgment.source.code}
                          {judgment.source.publisher && ` (${judgment.source.publisher})`}
                        </span>
                      )}
                      {judgment.source.publicationDate && (
                        <span className="text-xs text-slate-400 dark:text-slate-500">
                          {t('judgmentModal.publishedAt', { date: formatDate(judgment.source.publicationDate) })}
                        </span>
                      )}
                    </div>
                  </section>
                )}

                {/* Full text */}
                {judgment.textContent && (
                  <section>
                    <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                      {t('judgmentModal.fullText')}
                    </h3>
                    <div className="prose prose-slate dark:prose-invert max-w-none">
                      <div className="text-sm text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-slate-900 p-4 rounded-lg overflow-x-auto whitespace-pre-wrap">
                        {cleanTextContent(judgment.textContent)}
                      </div>
                    </div>
                  </section>
                )}
              </div>
            ) : null}
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-slate-50 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 px-6 py-3 flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
            >
              {t('judgmentModal.close')}
            </button>
            {judgment && (
              <button
                onClick={() => {
                  // Copy judgment info to clipboard
                  const text = `${judgment.caseNumber} - ${t(courtTypeKey(judgment.courtType), judgment.courtType)}`;
                  navigator.clipboard.writeText(text);
                }}
                className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {t('judgmentModal.copyCaseNumber')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Clean up text content that was extracted from HTML
 * - Strip HTML tags (block tags → newline, inline tags → nothing)
 * - Normalize excessive whitespace
 * - Reduce multiple blank lines to max 2
 */
function cleanTextContent(html: string): string {
  if (!html) return '';

  let text = html;

  // Remove HTML comments
  text = text.replace(/<!--[\s\S]*?-->/g, '');

  // Collapse whitespace inside inline tag sequences (handles nested tags with internal newlines)
  // e.g., <strong>\n<em>\n<span>K. J.</span>\n</em>\n</strong> → <strong><em><span>K. J.</span></em></strong>
  text = text.replace(/>\s+</g, '><');

  // Block-level tags that should create line breaks
  const blockTags = /(<\/?(p|div|br|h[1-6]|li|tr|th|td|table|blockquote|pre|hr|ul|ol|dl|dt|dd|section|article|header|footer|nav|aside|figure|figcaption)[^>]*>)/gi;
  text = text.replace(blockTags, '\n');

  // Remove all remaining HTML tags (inline tags like span, b, i, em, strong, a, etc.)
  text = text.replace(/<[^>]*>/g, '');

  // Decode HTML entities
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  text = textarea.value;

  // Replace tabs with spaces
  text = text.replace(/\t/g, ' ');

  // Normalize spaces within lines (but preserve newlines)
  text = text.replace(/[^\S\n]+/g, ' ');

  // Trim whitespace from each line
  text = text.split('\n').map(line => line.trim()).join('\n');

  // Reduce multiple consecutive blank lines to maximum of 2
  text = text.replace(/\n{3,}/g, '\n\n');

  // Trim leading/trailing whitespace
  text = text.trim();

  return text;
}
